import {
  YesNoAnswer,
  PolicyIssued,
  BuildingLevel,
  BuildingType,
  SumInsuredType,
} from 'scenes/common/types';
import {
  WireAsset,
  WireBuildingCoverSIOption,
} from 'scenes/common/api/quote/types';
import { WireSumInsured as WireSumHomeInsured } from 'scenes/home/api/quote/types';
import { WireSumInsured as WireSumLandlordInsured } from 'scenes/landlord/api/quote/types';

export const toAgreedBuildingSumInsured = (
  assets: WireAsset[],
  coverages?: WireSumHomeInsured[] | WireSumLandlordInsured[]
) => {
  const asset = assets[0];

  const allBuildingCoverages = (coverages ?? []).filter(
    (coverage) => coverage.type === 'Building'
  );
  if (
    asset?.buildingCoverSI?.selected === WireBuildingCoverSIOption.Manual &&
    allBuildingCoverages.length > 0
  ) {
    const buildingCoverage = allBuildingCoverages[0];
    return buildingCoverage.amount.toString();
  }

  return '';
};

// Estimated sum insured always recorded in assets.buildingCoverSI.estimated
export const toEstimatedSumInsured = (assets: WireAsset[]): string => {
  const asset = assets[0];
  return asset?.buildingCoverSI?.estimated.toString() ?? '';
};

export const toOwnedProperty = (assets: WireAsset[]): YesNoAnswer => {
  const asset = assets[0];
  if (asset.isOwned === undefined) {
    return YesNoAnswer.Unspecified;
  }
  if (asset.isOwned) {
    return YesNoAnswer.Yes;
  }
  return YesNoAnswer.No;
};

export const toBusinessType = (assets: WireAsset[]): string => {
  const asset = assets[0];
  if (asset?.businessSpecific?.businessType === undefined) {
    return ' ';
  }

  return asset.businessSpecific.businessType;
};

export const toUsedForBusiness = (assets: WireAsset[]): YesNoAnswer => {
  const asset = assets[0];
  if (asset?.isUsedForBusiness === undefined) {
    return YesNoAnswer.Unspecified;
  }
  if (asset?.isUsedForBusiness === true) {
    return YesNoAnswer.Yes;
  }

  return YesNoAnswer.No;
};

export const toPolicyIssued = (assets: WireAsset[]): PolicyIssued => {
  const asset = assets[0];
  return asset?.isUsedForBusiness ? PolicyIssued.Business : PolicyIssued.Person;
};

const getBuildingLevel = (level: string | undefined): BuildingLevel => {
  switch (level) {
    case BuildingLevel.Basement:
      return BuildingLevel.Basement;

    case BuildingLevel.GrdFloor:
      return BuildingLevel.GrdFloor;

    case BuildingLevel.Level1:
      return BuildingLevel.Level1;

    case BuildingLevel.Level2:
      return BuildingLevel.Level2;

    case BuildingLevel.Level3Plus:
      return BuildingLevel.Level3Plus;

    default:
      return BuildingLevel.Unspecified;
  }
};

const getBuildingType = (type: string | undefined): BuildingType => {
  switch (type) {
    case BuildingType.FreestandingHouse:
      return BuildingType.FreestandingHouse;

    case BuildingType.Unit:
      return BuildingType.Unit;

    case BuildingType.Terrace:
      return BuildingType.Terrace;

    case BuildingType.Townhouse:
      return BuildingType.Townhouse;

    case BuildingType.Other:
      return BuildingType.Other;

    default:
      return BuildingType.Unspecified;
  }
};

export const booleanToYesNoUnspecified = (value?: boolean): YesNoAnswer => {
  if (value === undefined) {
    return YesNoAnswer.Unspecified;
  }
  return value === true ? YesNoAnswer.Yes : YesNoAnswer.No;
};

const defaultBuildingInformation = {
  buildingType: BuildingType.Unspecified,
  freestandingMetreGap: YesNoAnswer.Unspecified,
  freestandingStorey: YesNoAnswer.Unspecified,
  terraceStorey: YesNoAnswer.Unspecified,
  terraceStrata: YesNoAnswer.Unspecified,
  townhouseStorey: YesNoAnswer.Unspecified,
  townhouseStrata: YesNoAnswer.Unspecified,
  unitStorey: YesNoAnswer.Unspecified,
  unitLevel: BuildingLevel.Unspecified,
  unitStrata: YesNoAnswer.Unspecified,
};

const mapToFreeStanding = (aboveGround?: boolean, multistorey?: boolean) => ({
  ...defaultBuildingInformation,
  buildingType: BuildingType.FreestandingHouse,
  freestandingMetreGap: booleanToYesNoUnspecified(aboveGround),
  freestandingStorey: booleanToYesNoUnspecified(multistorey),
});

const mapToTerrace = (multistorey?: boolean, strata?: boolean) => ({
  ...defaultBuildingInformation,
  buildingType: BuildingType.Terrace,
  terraceStorey: booleanToYesNoUnspecified(multistorey),
  terraceStrata: booleanToYesNoUnspecified(strata),
});

const mapToTownhouse = (multistorey?: boolean, strata?: boolean) => ({
  ...defaultBuildingInformation,
  buildingType: BuildingType.Townhouse,
  townhouseStorey: booleanToYesNoUnspecified(multistorey),
  townhouseStrata: booleanToYesNoUnspecified(strata),
});

const mapToUnit = (
  multistorey?: boolean,
  strata?: boolean,
  level?: string
) => ({
  ...defaultBuildingInformation,
  buildingType: BuildingType.Unit,
  unitStorey: booleanToYesNoUnspecified(multistorey),
  unitLevel: getBuildingLevel(level),
  unitStrata: booleanToYesNoUnspecified(strata),
});

export const toBuildingInformation = (assets: WireAsset[]) => {
  const asset = assets[0];
  const buildingType = getBuildingType(asset?.building?.type);
  switch (buildingType) {
    case BuildingType.FreestandingHouse:
      return mapToFreeStanding(
        asset?.building?.isAboveGround,
        asset?.building?.isMultiStory
      );
    case BuildingType.Terrace:
      return mapToTerrace(
        asset?.building?.isMultiStory,
        asset?.building?.isUnderStrata
      );
    case BuildingType.Townhouse:
      return mapToTownhouse(
        asset?.building?.isMultiStory,
        asset?.building?.isUnderStrata
      );
    case BuildingType.Unit:
      return mapToUnit(
        asset?.building?.isMultiStory,
        asset?.building?.isUnderStrata,
        asset?.building?.level
      );
    case BuildingType.Other:
      return {
        ...defaultBuildingInformation,
        buildingType: BuildingType.Other,
      };
    default:
      return { ...defaultBuildingInformation, buildingType };
  }
};

// Only unmarshal additional security installed to 'Yes' or 'No'
// if broken in claims is Yes. Otherwise risks unwanted default behaviour.
export const toAdditionalSecurityInstalled = (
  assets: WireAsset[],
  brokenInClaims: YesNoAnswer
): YesNoAnswer =>
  brokenInClaims === YesNoAnswer.Yes
    ? booleanToYesNoUnspecified(assets[0]?.hasAddSecurity)
    : YesNoAnswer.Unspecified;

export const toNumberOfBedrooms = (assets: WireAsset[]): number => {
  const numberOfRooms = assets[0]?.building?.numberOfRooms;

  return numberOfRooms || 0;
};

export const toPropertyBuiltYear = (assets: WireAsset[]): string => {
  const years = assets[0]?.building?.year;

  return years || '';
};

export const toRoofMaterial = (assets: WireAsset[]): string => {
  const asset = assets[0];

  return asset?.building?.roof || '';
};

export const toWallMaterial = (assets: WireAsset[]): string => {
  const asset = assets[0];

  return asset?.building?.wall || '';
};

export const toSecurityFeatures = (assets: WireAsset[]): string[] => {
  const asset = assets[0];
  const features = asset?.securities?.map((item) => item.code) || [];

  return features;
};

export const toSumInsuredType = (assets: WireAsset[]): SumInsuredType => {
  const asset = assets[0];
  const selected = asset?.buildingCoverSI?.selected;

  switch (selected) {
    case WireBuildingCoverSIOption.Estimated:
      return SumInsuredType.Estimated;
    case WireBuildingCoverSIOption.Manual:
      return SumInsuredType.Agreed;
    case WireBuildingCoverSIOption.Unspecified:
      return SumInsuredType.Unspecified;

    default:
      return SumInsuredType.Unspecified;
  }
};
